import { ActionIcon } from '@any-ui-react/core';
import { useMemo } from 'react';

import { useDeleteRecordModal } from '@/common/components/shared/delete-record-modal/useDeleteRecordModal';
import { MaterialIcons } from '@/common/components/shared/material-icons';
import { NoResult } from '@/common/components/shared/no-result/NoResult';
import { EMPTY_DEFAULT } from '@/common/components/shared/table/constants';
import { Table, type TableLayout } from '@/common/components/shared/table/Table';
import { useEmployeeDetailContext } from '@/common/contexts/EmployeeDetailContext';
import { useEmployeeAccess } from '@/common/hooks/employees/useEmployeeAccess';
import type { EmployeeVisaType } from '@/common/types';
import { DATETIME_FORMATS, DateUtils } from '@/common/utils';

import { VisaModal } from './VisaModal';

interface VisaTableProps {
  loading: boolean;
  items: EmployeeVisaType[];
  onRemove: (id: string) => Promise<void>;
  onUpdate: (data: EmployeeVisaType) => Promise<void>;
  isEditable?: boolean;
}

export const VisaTable = ({ items, loading, onRemove, onUpdate, isEditable }: VisaTableProps) => {
  const { openDeleteRecordModal } = useDeleteRecordModal();

  const { employeeDetail } = useEmployeeDetailContext();
  const isNewRecord = !employeeDetail.id;
  const { hasEmployeePersonalInformationDeleteAccess } = useEmployeeAccess();
  const isRemovable = hasEmployeePersonalInformationDeleteAccess || isNewRecord;

  const layout: Array<TableLayout<EmployeeVisaType>> = useMemo(
    () => [
      {
        headerRender: () => 'Visa Type',
        accessor: 'visaType',
        truncated: false,
        cellRender: (visa: EmployeeVisaType) =>
          isEditable ? (
            <VisaModal className="ml-auto" visa={visa} onSuccess={onUpdate}>
              <span className="text-blue-6 cursor-pointer">{visa.visaType}</span>
            </VisaModal>
          ) : (
            visa.visaType
          ),
      },
      {
        headerRender: () => 'Visa Number',
        accessor: 'visaNumber',
        cellRender: ({ visaNumber }) => visaNumber,
      },
      {
        headerRender: () => 'Visa Issued Date',
        accessor: 'visaIssuedDate',
        thClassName: 'text-right',
        tdClassName: 'text-right',
        cellRender: ({ visaIssuedDate }) => {
          const { text, isFuture } = DateUtils.getDifferenceString(visaIssuedDate);

          return (
            <>
              {DateUtils.formatDate(visaIssuedDate, DATETIME_FORMATS.MMMddyyyy) || EMPTY_DEFAULT}
              <div className="text-gray-7 text-2xs">
                {text}
                {!isFuture ? ' ago' : ''}
              </div>
            </>
          );
        },
      },

      {
        headerRender: () => 'Visa Expiry Date',
        accessor: 'visaExpiryDate',
        thClassName: 'text-right',
        tdClassName: 'text-right',
        cellRender: ({ visaExpiryDate }) => {
          const { text, isFuture } = DateUtils.getDifferenceString(visaExpiryDate);

          return (
            <>
              {DateUtils.formatDate(visaExpiryDate, DATETIME_FORMATS.MMMddyyyy) || EMPTY_DEFAULT}
              <div className="text-gray-7 text-2xs">
                {text}
                {!isFuture ? ' ago' : ''}
              </div>
            </>
          );
        },
      },

      {
        headerRender: () => 'Work Permit Number',
        accessor: 'workPermitNumber',
        cellRender: ({ workPermitNumber }) => workPermitNumber,
      },
      {
        headerRender: () => 'Work Permit Issued Date',
        accessor: 'workPermitIssuedDate',
        thClassName: 'text-right',
        tdClassName: 'text-right',
        cellRender: ({ workPermitIssuedDate }) => {
          const { text, isFuture } = DateUtils.getDifferenceString(workPermitIssuedDate);

          return (
            <>
              {DateUtils.formatDate(workPermitIssuedDate, DATETIME_FORMATS.MMMddyyyy) || EMPTY_DEFAULT}
              <div className="text-gray-7 text-2xs">
                {text}
                {!isFuture ? ' ago' : ''}
              </div>
            </>
          );
        },
      },
      {
        headerRender: () => 'Work Permit Expiry Date',
        accessor: 'workPermitExpiryDate',
        thClassName: 'text-right',
        tdClassName: 'text-right',
        cellRender: ({ workPermitExpiryDate }) => {
          const { text, isFuture } = DateUtils.getDifferenceString(workPermitExpiryDate);

          return (
            <>
              {DateUtils.formatDate(workPermitExpiryDate, DATETIME_FORMATS.MMMddyyyy) || EMPTY_DEFAULT}
              <div className="text-gray-7 text-2xs">
                {text}
                {!isFuture ? ' ago' : ''}
              </div>
            </>
          );
        },
      },
      ...(isRemovable
        ? [
            {
              headerRender: () => '',
              accessor: 'delete',
              thClassName: '!max-w-[72px]',
              tdClassName: '!max-w-[72px]',
              cellRender: ({ id }: EmployeeVisaType) => (
                <ActionIcon
                  className="ml-auto"
                  size="xs"
                  onClick={() => {
                    openDeleteRecordModal({
                      onSuccess: () => onRemove(id),
                    });
                  }}
                >
                  <MaterialIcons iconName="delete" className="cursor-pointer" />
                </ActionIcon>
              ),
            },
          ]
        : []),
    ],
    [isEditable, isRemovable, onRemove, onUpdate, openDeleteRecordModal],
  );

  return (
    <Table<EmployeeVisaType>
      allowFloatingHeader={true}
      data={items || []}
      emptyContainerClassName="border-gray-2 text-center"
      headerClassName="[&_th]:border-t-0"
      layout={layout}
      loading={loading}
      notFoundTemplate={
        <NoResult title="No visa found" titleClassName="text-sm text-gray-9" wrapperClassName="py-10" />
      }
      numOfStickyCols={0}
      wrapperClassName="border"
    />
  );
};
